// AUTO-GENERATED FILE - DO NOT EDIT
// This file was automatically generated by the XDK build tool.
// Any manual changes will be overwritten on the next generation.

/**
 * webhooks client for the X API.
 *
 * This module provides a client for interacting with the webhooks endpoints of the X API.
 */

import { Client, ApiResponse, RequestOptions } from '../client.js';
import { 
    Paginator, 
    PostPaginator, 
    UserPaginator, 
    EventPaginator
} from '../paginator.js';
import {
GetResponse,
CreateRequest,
CreateResponse,
GetStreamLinksResponse,
ValidateResponse,
DeleteResponse,
CreateStreamLinkResponse,
DeleteStreamLinkResponse,
CreateWebhookReplayJobRequest,
CreateWebhookReplayJobResponse,
} from './models.js';


/**
 * Options for get method
 * 
 * @public
 */
export interface GetOptions {
    
    
    /** A comma separated list of WebhookConfig fields to display. 
     * Also accepts: webhook_config.fields or proper camelCase (e.g., webhookConfigFields) */
    webhookConfigFields?: Array<any>;
    
    
    
    /** Additional request options */
    requestOptions?: RequestOptions;
    /** Allow original API parameter names (e.g., 'tweet.fields', 'user.fields') and proper camelCase (e.g., 'tweetFields', 'userFields') */
    [key: string]: any;
}


/**
 * Options for create method
 * 
 * @public
 */
export interface CreateOptions {
    
    
    /** Request body */
    body?: CreateRequest;
    
    /** Additional request options */
    requestOptions?: RequestOptions;
    /** Allow original API parameter names (e.g., 'tweet.fields', 'user.fields') and proper camelCase (e.g., 'tweetFields', 'userFields') */
    [key: string]: any;
}





/**
 * Options for createStreamLink method
 * 
 * @public
 */
export interface CreateStreamLinkOptions {
    
    
    /** A comma separated list of Tweet fields to display. 
     * Also accepts: tweet.fields or proper camelCase (e.g., tweetFields) */
    tweetFields?: string;
    
    
    
    /** A comma separated list of fields to expand. 
     * Also accepts: expansions or proper camelCase (e.g., expansions) */
    expansions?: string;
    
    
    
    /** A comma separated list of Media fields to display. 
     * Also accepts: media.fields or proper camelCase (e.g., mediaFields) */
    mediaFields?: string;
    
    
    
    /** A comma separated list of Poll fields to display. 
     * Also accepts: poll.fields or proper camelCase (e.g., pollFields) */
    pollFields?: string;
    
    
    
    /** A comma separated list of User fields to display. 
     * Also accepts: user.fields or proper camelCase (e.g., userFields) */
    userFields?: string;
    
    
    
    /** A comma separated list of Place fields to display. 
     * Also accepts: place.fields or proper camelCase (e.g., placeFields) */
    placeFields?: string;
    
    
    
    /** Additional request options */
    requestOptions?: RequestOptions;
    /** Allow original API parameter names (e.g., 'tweet.fields', 'user.fields') and proper camelCase (e.g., 'tweetFields', 'userFields') */
    [key: string]: any;
}



/**
 * Options for createWebhookReplayJob method
 * 
 * @public
 */
export interface CreateWebhookReplayJobOptions {
    
    
    /** Request body */
    body?: CreateWebhookReplayJobRequest;
    
    /** Additional request options */
    requestOptions?: RequestOptions;
    /** Allow original API parameter names (e.g., 'tweet.fields', 'user.fields') and proper camelCase (e.g., 'tweetFields', 'userFields') */
    [key: string]: any;
}



/**
 * Client for webhooks operations
 * 
 * This client provides methods for interacting with the webhooks endpoints
 * of the X API. It handles authentication, request formatting, and response
 * parsing for all webhooks related operations.
 * 
 * @category webhooks
 */
export class WebhooksClient {
    private client: Client;

    /**
     * Creates a new webhooks client instance
     * 
     * @param client - The main X API client instance
     */
    constructor(client: Client) {
        this.client = client;
    }

    /**
     * Normalize options object to handle both camelCase and original API parameter names
     * Only accepts: proper camelCase (tweetFields) and original API format (tweet.fields)
     */
    private _normalizeOptions<T extends Record<string, any>>(options: T, paramMappings: Record<string, string>): T {
        if (!options || typeof options !== 'object') {
            return options;
        }
        
        const normalized: any = { ...options };
        
        // For each parameter mapping (original -> proper camelCase)
        for (const [originalName, camelName] of Object.entries(paramMappings)) {
            // Check if original format is used (e.g., 'tweet.fields', 'tweet_fields')
            if (originalName in normalized && !(camelName in normalized)) {
                normalized[camelName] = normalized[originalName];
                delete normalized[originalName];
            }
            // Also check for proper camelCase (e.g., 'tweetFields')
            // If it's already in proper camelCase, keep it (no conversion needed)
            // The camelName is already the proper camelCase format
        }
        
        return normalized as T;
    }



  /**
   * Get webhook
   * Get a list of webhook configs associated with a client app.



   * @returns {Promise<GetResponse>} Promise resolving to the API response
   */
    // Overload 1: Default behavior (unwrapped response)
    async get(
        
        
        
        
        
        
        
        
        options: GetOptions = {}
        
    ): Promise<GetResponse> {
        // Normalize options to handle both camelCase and original API parameter names
        
        
        const paramMappings: Record<string, string> = {
            
            
            'webhook_config.fields': 'webhookConfigFields',
            
            
        };
        const normalizedOptions = this._normalizeOptions(options || {}, paramMappings);
        
        
        // Destructure options (exclude path parameters, they're already function params)
        const {
            
            
            webhookConfigFields = [],
            
            
            
            requestOptions: requestOptions = {}
        } = normalizedOptions;
        

        // Build the path with path parameters
        let path = '/2/webhooks';
        

        // Build query parameters
        const params = new URLSearchParams();
        
        
        
        
        if (webhookConfigFields !== undefined && webhookConfigFields.length > 0) {
            
            params.append('webhook_config.fields', webhookConfigFields.join(','));
            
        }
        
        
        

        // Prepare request options
        const finalRequestOptions: RequestOptions = {
            
            
            // Pass security requirements for smart auth selection
            security: [
                
                {
                    
                    'BearerToken': [],
                    
                }
                
            ],
            
            
            ...requestOptions
            
        };

        return this.client.request<GetResponse>(
            'GET',
            path + (params.toString() ? `?${params.toString()}` : ''),
            finalRequestOptions
        );
    }




  /**
   * Create webhook
   * Creates a new webhook configuration.



   * @returns {Promise<CreateResponse>} Promise resolving to the API response
   */
    // Overload 1: Default behavior (unwrapped response)
    async create(
        
        
        
        
        
        
        
        
        options: CreateOptions = {}
        
    ): Promise<CreateResponse> {
        // Normalize options to handle both camelCase and original API parameter names
        
        
        const normalizedOptions = options || {};
        
        
        // Destructure options (exclude path parameters, they're already function params)
        const {
            
            
            body,
            
            requestOptions: requestOptions = {}
        } = normalizedOptions;
        

        // Build the path with path parameters
        let path = '/2/webhooks';
        

        // Build query parameters
        const params = new URLSearchParams();
        

        // Prepare request options
        const finalRequestOptions: RequestOptions = {
            
            body: body ? JSON.stringify(body) : undefined,
            
            
            // Pass security requirements for smart auth selection
            security: [
                
                {
                    
                    'BearerToken': [],
                    
                },
                
                {
                    
                    'UserToken': [],
                    
                }
                
            ],
            
            
            ...requestOptions
            
        };

        return this.client.request<CreateResponse>(
            'POST',
            path + (params.toString() ? `?${params.toString()}` : ''),
            finalRequestOptions
        );
    }




  /**
   * Get stream links
   * Get a list of webhook links associated with a filtered stream ruleset.



   * @returns {Promise<GetStreamLinksResponse>} Promise resolving to the API response
   */
    // Overload 1: Default behavior (unwrapped response)
    async getStreamLinks(
        
        
        
        
        
        
        
        
    ): Promise<GetStreamLinksResponse> {
        // Normalize options to handle both camelCase and original API parameter names
        
        const requestOptions = {};
        

        // Build the path with path parameters
        let path = '/2/tweets/search/webhooks';
        

        // Build query parameters
        const params = new URLSearchParams();
        

        // Prepare request options
        const finalRequestOptions: RequestOptions = {
            
            
            // Pass security requirements for smart auth selection
            security: [
                
                {
                    
                    'BearerToken': [],
                    
                }
                
            ],
            
            
            // No optional parameters, using empty request options
            
        };

        return this.client.request<GetStreamLinksResponse>(
            'GET',
            path + (params.toString() ? `?${params.toString()}` : ''),
            finalRequestOptions
        );
    }




  /**
   * Validate webhook
   * Triggers a CRC check for a given webhook.


   * @param webhookId The ID of the webhook to check.




   * @returns {Promise<ValidateResponse>} Promise resolving to the API response
   */
    // Overload 1: Default behavior (unwrapped response)
    async validate(
        
        
        
        webhookId: string,
        
        
        
        
        
        
        
        
    ): Promise<ValidateResponse> {
        // Normalize options to handle both camelCase and original API parameter names
        
        const requestOptions = {};
        

        // Build the path with path parameters
        let path = '/2/webhooks/{webhook_id}';
        
        
        path = path.replace('{webhook_id}', encodeURIComponent(String(webhookId)));
        
        

        // Build query parameters
        const params = new URLSearchParams();
        

        // Prepare request options
        const finalRequestOptions: RequestOptions = {
            
            
            // Pass security requirements for smart auth selection
            security: [
                
                {
                    
                    'BearerToken': [],
                    
                },
                
                {
                    
                    'UserToken': [],
                    
                }
                
            ],
            
            
            // No optional parameters, using empty request options
            
        };

        return this.client.request<ValidateResponse>(
            'PUT',
            path + (params.toString() ? `?${params.toString()}` : ''),
            finalRequestOptions
        );
    }




  /**
   * Delete webhook
   * Deletes an existing webhook configuration.


   * @param webhookId The ID of the webhook to delete.




   * @returns {Promise<DeleteResponse>} Promise resolving to the API response
   */
    // Overload 1: Default behavior (unwrapped response)
    async delete(
        
        
        
        webhookId: string,
        
        
        
        
        
        
        
        
    ): Promise<DeleteResponse> {
        // Normalize options to handle both camelCase and original API parameter names
        
        const requestOptions = {};
        

        // Build the path with path parameters
        let path = '/2/webhooks/{webhook_id}';
        
        
        path = path.replace('{webhook_id}', encodeURIComponent(String(webhookId)));
        
        

        // Build query parameters
        const params = new URLSearchParams();
        

        // Prepare request options
        const finalRequestOptions: RequestOptions = {
            
            
            // Pass security requirements for smart auth selection
            security: [
                
                {
                    
                    'BearerToken': [],
                    
                },
                
                {
                    
                    'UserToken': [],
                    
                }
                
            ],
            
            
            // No optional parameters, using empty request options
            
        };

        return this.client.request<DeleteResponse>(
            'DELETE',
            path + (params.toString() ? `?${params.toString()}` : ''),
            finalRequestOptions
        );
    }




  /**
   * Create stream link
   * Creates a link to deliver FilteredStream events to the given webhook.


   * @param webhookId The webhook ID to link to your FilteredStream ruleset.




   * @returns {Promise<CreateStreamLinkResponse>} Promise resolving to the API response
   */
    // Overload 1: Default behavior (unwrapped response)
    async createStreamLink(
        
        
        
        webhookId: string,
        
        
        
        
        
        
        
        
        options: CreateStreamLinkOptions = {}
        
    ): Promise<CreateStreamLinkResponse> {
        // Normalize options to handle both camelCase and original API parameter names
        
        
        const paramMappings: Record<string, string> = {
            
            
            'tweet.fields': 'tweetFields',
            
            
            
            
            
            'media.fields': 'mediaFields',
            
            
            
            'poll.fields': 'pollFields',
            
            
            
            'user.fields': 'userFields',
            
            
            
            'place.fields': 'placeFields',
            
            
        };
        const normalizedOptions = this._normalizeOptions(options || {}, paramMappings);
        
        
        // Destructure options (exclude path parameters, they're already function params)
        const {
            
            
            tweetFields = undefined,
            
            
            
            expansions = undefined,
            
            
            
            mediaFields = undefined,
            
            
            
            pollFields = undefined,
            
            
            
            userFields = undefined,
            
            
            
            placeFields = undefined,
            
            
            
            requestOptions: requestOptions = {}
        } = normalizedOptions;
        

        // Build the path with path parameters
        let path = '/2/tweets/search/webhooks/{webhook_id}';
        
        
        path = path.replace('{webhook_id}', encodeURIComponent(String(webhookId)));
        
        

        // Build query parameters
        const params = new URLSearchParams();
        
        
        
        
        if (tweetFields !== undefined) {
            
            params.append('tweet.fields', String(tweetFields));
            
        }
        
        
        
        
        
        
        if (expansions !== undefined) {
            
            params.append('expansions', String(expansions));
            
        }
        
        
        
        
        
        
        if (mediaFields !== undefined) {
            
            params.append('media.fields', String(mediaFields));
            
        }
        
        
        
        
        
        
        if (pollFields !== undefined) {
            
            params.append('poll.fields', String(pollFields));
            
        }
        
        
        
        
        
        
        if (userFields !== undefined) {
            
            params.append('user.fields', String(userFields));
            
        }
        
        
        
        
        
        
        if (placeFields !== undefined) {
            
            params.append('place.fields', String(placeFields));
            
        }
        
        
        

        // Prepare request options
        const finalRequestOptions: RequestOptions = {
            
            
            // Pass security requirements for smart auth selection
            security: [
                
                {
                    
                    'BearerToken': [],
                    
                }
                
            ],
            
            
            ...requestOptions
            
        };

        return this.client.request<CreateStreamLinkResponse>(
            'POST',
            path + (params.toString() ? `?${params.toString()}` : ''),
            finalRequestOptions
        );
    }




  /**
   * Delete stream link
   * Deletes a link from FilteredStream events to the given webhook.


   * @param webhookId The webhook ID to link to your FilteredStream ruleset.




   * @returns {Promise<DeleteStreamLinkResponse>} Promise resolving to the API response
   */
    // Overload 1: Default behavior (unwrapped response)
    async deleteStreamLink(
        
        
        
        webhookId: string,
        
        
        
        
        
        
        
        
    ): Promise<DeleteStreamLinkResponse> {
        // Normalize options to handle both camelCase and original API parameter names
        
        const requestOptions = {};
        

        // Build the path with path parameters
        let path = '/2/tweets/search/webhooks/{webhook_id}';
        
        
        path = path.replace('{webhook_id}', encodeURIComponent(String(webhookId)));
        
        

        // Build query parameters
        const params = new URLSearchParams();
        

        // Prepare request options
        const finalRequestOptions: RequestOptions = {
            
            
            // Pass security requirements for smart auth selection
            security: [
                
                {
                    
                    'BearerToken': [],
                    
                }
                
            ],
            
            
            // No optional parameters, using empty request options
            
        };

        return this.client.request<DeleteStreamLinkResponse>(
            'DELETE',
            path + (params.toString() ? `?${params.toString()}` : ''),
            finalRequestOptions
        );
    }




  /**
   * Create replay job for webhook
   * Creates a replay job to retrieve events from up to the past 24 hours for all events delivered or attempted to be delivered to the webhook.



   * @returns {Promise<CreateWebhookReplayJobResponse>} Promise resolving to the API response
   */
    // Overload 1: Default behavior (unwrapped response)
    async createWebhookReplayJob(
        
        
        
        
        
        
        
        
        options: CreateWebhookReplayJobOptions = {}
        
    ): Promise<CreateWebhookReplayJobResponse> {
        // Normalize options to handle both camelCase and original API parameter names
        
        
        const normalizedOptions = options || {};
        
        
        // Destructure options (exclude path parameters, they're already function params)
        const {
            
            
            body,
            
            requestOptions: requestOptions = {}
        } = normalizedOptions;
        

        // Build the path with path parameters
        let path = '/2/webhooks/replay';
        

        // Build query parameters
        const params = new URLSearchParams();
        

        // Prepare request options
        const finalRequestOptions: RequestOptions = {
            
            body: body ? JSON.stringify(body) : undefined,
            
            
            // Pass security requirements for smart auth selection
            security: [
                
                {
                    
                    'BearerToken': [],
                    
                }
                
            ],
            
            
            ...requestOptions
            
        };

        return this.client.request<CreateWebhookReplayJobResponse>(
            'POST',
            path + (params.toString() ? `?${params.toString()}` : ''),
            finalRequestOptions
        );
    }





















}
